import { useState } from "react";
import axios from "axios";
import { NC } from "../landing/shared";
import { APPLICATION_URL, APPLY_CTA_LABEL } from "./config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONSENT_TEXT =
  "By checking this box, I provide my express written consent to be contacted by Shoreline Mortgage Group by phone call, text message, and email at the number and email address provided, including through automated technology and prerecorded messages. Consent is not a condition of any purchase or service. Message and data rates may apply. Reply STOP to opt out at any time.";

const SITUATIONS = [
  "Planning a first purchase",
  "Have used VA-backed financing before",
  "Own a home and considering a move-up",
  "Was told no by another lender",
];

const getAttribution = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_content: p.get("utm_content"),
    utm_term: p.get("utm_term"),
    fbclid: p.get("fbclid"),
    gclid: p.get("gclid"),
    contact_id: p.get("contact_id"),
    page_url: window.location.href,
    referrer_url: document.referrer || null,
    landed_at: new Date().toISOString(),
  };
};

export default function ApplyForm() {
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: params.get("email") || "",
    phone: "",
    state: "",
    buyerSituation: "",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.state) return setError("Please select your state.");
    if (!consent) return setError("Please check the consent box to continue.");
    setBusy(true);
    try {
      await axios.post(`${API}/replay-lead`, {
        ...form,
        buyerSituation: form.buyerSituation || null,
        videoWatchPct: 0,
        consent: true,
        consentText: CONSENT_TEXT,
        attribution: getAttribution(),
      });
      setDone(true);
      if (APPLICATION_URL) window.location.href = APPLICATION_URL;
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[#0162CA] bg-[#0162CA]/10 p-8 text-center" data-testid="apply-success-card">
        <div className="text-[#0162CA] text-4xl mb-4">✓</div>
        <h3 className="text-xl font-black uppercase tracking-tight mb-3">Request received.</h3>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          Your next step is completing the mortgage application and required authorizations. Josh's team will
          follow up to schedule your one-on-one Better Deal Review™ once your information is complete.
        </p>
        {APPLICATION_URL ? (
          <a href={APPLICATION_URL} data-cursor="cta" className="cta-btn w-full" data-testid="apply-continue-link">
            CONTINUE TO SECURE APPLICATION ↗
          </a>
        ) : (
          <p className="text-sm"><NC label="APPLICATION URL" /></p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="apply-form" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <input required className="form-input" placeholder="First name" value={form.firstName} onChange={set("firstName")} data-testid="apply-first-name-input" />
        <input required className="form-input" placeholder="Last name" value={form.lastName} onChange={set("lastName")} data-testid="apply-last-name-input" />
      </div>
      <input required type="email" className="form-input" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="apply-email-input" />
      <input required type="tel" className="form-input" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} data-testid="apply-phone-input" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select required className="form-input" value={form.state} onChange={set("state")} data-testid="apply-state-select">
          <option value="">State *</option>
          <option value="Florida">Florida</option>
          <option value="Pennsylvania">Pennsylvania</option>
        </select>
        <select className="form-input" value={form.buyerSituation} onChange={set("buyerSituation")} data-testid="apply-situation-select">
          <option value="">What best describes you?</option>
          {SITUATIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <label className="flex items-start gap-3 cursor-pointer pt-1">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#0162CA]"
          data-testid="apply-consent-checkbox"
        />
        <span className="text-[10px] leading-relaxed text-white/35">
          {CONSENT_TEXT}{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">Privacy Policy</a> ·{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">Terms</a>
        </span>
      </label>
      {error && <p className="text-red-400 text-xs" data-testid="apply-form-error">{error}</p>}
      <button type="submit" disabled={busy} data-cursor="cta" className="cta-btn w-full disabled:opacity-60" data-testid="apply-submit-button">
        {busy ? "SUBMITTING…" : APPLY_CTA_LABEL}
      </button>
      <p className="text-[10px] text-white/25 text-center">
        Free review · No fee to you · Nothing here is an approval or a commitment to lend
      </p>
    </form>
  );
}
